import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import ForceGraph3D from 'react-force-graph-3d';
import { useNavigate } from 'react-router-dom';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { objectsStore } from "@/lib/stores/objectsStore";
import { stringsStore } from '@/lib/stores/stringsStore';
import { templatesStore } from "@/lib/stores/templatesStore";
import { documentStore } from '@/lib/stores/documentStore';
import { ENTITY_TYPE_COLORS } from '@/lib/utils/entityColors';
import { forceCollide } from 'd3-force-3d';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { useZoom } from '@/providers/ZoomProvider';
import { useTheme } from '@/providers/ThemeProvider';
import { 
  ReactFlow, 
  Node, 
  Edge, 
  Background, 
  Controls, 
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

export interface EntityRelationship {
  game_id: string;
  source_id: string;
  source_type: string;
  target_id: string;
  target_type: string;
  relationship_context?: string;
  created_at?: string;
}

interface GraphNode {
  id: string;
  name: string;
  type: string;
  val: number;
  sourceText?: string; // For text entities
}

interface GraphLink {
  source: string;
  target: string;
  context?: string;
}

interface EntityRelationshipGraphProps {
  relationships: EntityRelationship[];
  width?: number;
  height?: number;
  initialSelectedNodeId?: string;
  showControls?: boolean;
  showLegend?: boolean;
  zoomPadding?: number;
  disableZoomCompensation?: boolean;
  onNodeClick?: (entityId: string, entityType: string) => void;
}

// Use centralized color mapping for different entity types
const typeColors = ENTITY_TYPE_COLORS;

export const EntityRelationshipGraph: React.FC<EntityRelationshipGraphProps> = ({
  relationships,
  width = 1200,
  height = 800,
  initialSelectedNodeId,
  showControls = true,
  showLegend = true,
  zoomPadding = 100,
  disableZoomCompensation = false,
  onNodeClick,
}) => {
  const { t } = usePlatformTranslation();
  const graphRef = useRef<any>();
  const navigate = useNavigate();
  const { selectedGame } = useSimplifiedUserData();
  const { zoom } = useZoom();
  const { effectiveTheme } = useTheme();
  const isDarkMode = effectiveTheme === 'dark';
  
  // State for filtering and view mode
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(initialSelectedNodeId || null);
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const [hiddenLabels, setHiddenLabels] = useState<Set<string>>(new Set(['text', 'types', 'type-values']));
  const [viewMode, setViewMode] = useState<'force-2d' | 'force-3d' | 'flow'>('force-2d');
  const [hasInitiallyZoomed, setHasInitiallyZoomed] = useState<boolean>(false);
  
  // Layout modes: 'default', 'tight', 'spread', 'hierarchical', 'radial', 'tree'
  const [layoutMode, setLayoutMode] = useState<'default' | 'tight' | 'spread' | 'hierarchical' | 'radial' | 'tree'>('default');
  
  // React Flow layout modes
  const [flowLayoutMode, setFlowLayoutMode] = useState<'circular' | 'grid' | 'horizontal-tree' | 'vertical-tree' | 'radial' | 'force'>('vertical-tree');
  
  // DAG mode for directed graphs
  const [dagMode, setDagMode] = useState<'td' | 'bu' | 'lr' | 'rl' | 'radialout' | 'radialin' | null>(null);
  const [showDagError, setShowDagError] = useState<boolean>(false);
  const [showCyclesOverview, setShowCyclesOverview] = useState<boolean>(false);

  // Update selected node when initialSelectedNodeId prop changes
  useEffect(() => {
    if (initialSelectedNodeId !== undefined) {
      setSelectedNodeId(initialSelectedNodeId || null);
    }
  }, [initialSelectedNodeId]);

  // Detect cycles in the graph using DFS and return all cycles found
  const detectCyclesWithPaths = useCallback((links: GraphLink[]): string[][] => {
    const adjacency = new Map<string, string[]>();
    const visited = new Set<string>();
    const recursionStack = new Set<string>();
    const path: string[] = [];
    const cycles: string[][] = [];

    // Build adjacency list
    links.forEach(link => {
      const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
      const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
      
      if (!adjacency.has(sourceId)) {
        adjacency.set(sourceId, []);
      }
      adjacency.get(sourceId)!.push(targetId);
    });

    // DFS to detect cycles and record paths
    const findCyclesDFS = (nodeId: string): void => {
      visited.add(nodeId);
      recursionStack.add(nodeId);
      path.push(nodeId);

      const neighbors = adjacency.get(nodeId) || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          findCyclesDFS(neighbor);
        } else if (recursionStack.has(neighbor)) {
          // Found a back edge (cycle) - extract the cycle path
          const cycleStartIndex = path.indexOf(neighbor);
          if (cycleStartIndex !== -1) {
            const cycle = path.slice(cycleStartIndex);
            cycle.push(neighbor); // Complete the cycle
            
            // Check if this cycle is not a duplicate (same nodes, different starting point)
            const cycleSet = new Set(cycle.slice(0, -1));
            const isDuplicate = cycles.some(existingCycle => {
              const existingSet = new Set(existingCycle.slice(0, -1));
              return cycleSet.size === existingSet.size && 
                     [...cycleSet].every(node => existingSet.has(node));
            });
            
            if (!isDuplicate) {
              cycles.push(cycle);
            }
          }
        }
      }

      recursionStack.delete(nodeId);
      path.pop();
    };

    // Check all nodes
    for (const nodeId of adjacency.keys()) {
      if (!visited.has(nodeId)) {
        findCyclesDFS(nodeId);
      }
    }

    return cycles;
  }, []);

  // Simple boolean check for cycles
  const detectCycles = useCallback((links: GraphLink[]): boolean => {
    const adjacency = new Map<string, string[]>();
    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    // Build adjacency list
    links.forEach(link => {
      const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
      const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
      
      if (!adjacency.has(sourceId)) {
        adjacency.set(sourceId, []);
      }
      adjacency.get(sourceId)!.push(targetId);
    });

    // DFS to detect cycle
    const hasCycleDFS = (nodeId: string): boolean => {
      visited.add(nodeId);
      recursionStack.add(nodeId);

      const neighbors = adjacency.get(nodeId) || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          if (hasCycleDFS(neighbor)) {
            return true;
          }
        } else if (recursionStack.has(neighbor)) {
          // Found a back edge (cycle)
          return true;
        }
      }

      recursionStack.delete(nodeId);
      return false;
    };

    // Check all nodes
    for (const nodeId of adjacency.keys()) {
      if (!visited.has(nodeId)) {
        if (hasCycleDFS(nodeId)) {
          return true;
        }
      }
    }

    return false;
  }, []);

  // Helper function to get entity name by ID and type
  const getEntityName = useCallback((entityId: string, entityType: string): { name: string; sourceText?: string } => {
    const allGlossaryItems = objectsStore.getData();
    const allStringsItems = stringsStore.getData();
    const allBlueprintItems = templatesStore.getData();
    const allDocumentItems = documentStore.getData();

    switch (entityType) {
      case 'glossary': {
        const item = allGlossaryItems.find(i => i.id === entityId && (i.type === 'glossary' || i.type === 'Library Object'));
        return { name: item?.name || 'Unknown Item' };
      }
      case 'document': {
        // Check document store first
        const docItem = allDocumentItems.find(i => i.id === entityId);
        if (docItem) {
          return { name: docItem.name };
        }
        // Fallback to glossary store (in case document is stored there)
        const glossaryDocItem = allGlossaryItems.find(i => i.id === entityId && i.type === 'document');
        return { name: glossaryDocItem?.name || 'Unknown Document' };
      }
      case 'text': {
        const item = allStringsItems.find(i => i.id === entityId);
        const locaKey = item?.abbrev || '';
        const sourceText = item?.source_text || '';
        // For text items, show loca-key as name
        const displayName = locaKey || sourceText.substring(0, 30) + (sourceText.length > 30 ? '...' : '') || 'Unknown Text';
        return { 
          name: displayName,
          sourceText: sourceText
        };
      }
      case 'blueprint': {
        const item = allBlueprintItems.find(i => i.id === entityId);
        return { name: item?.name || 'Unknown Template' };
      }
      default:
        return { name: 'Unknown Item' };
    }
  }, []);

  // Transform relationships into nodes and links
  const fullGraphData = useMemo(() => {
    const nodeMap = new Map<string, GraphNode>();
    const links: GraphLink[] = [];

    relationships.forEach((rel) => {
      // Add source node
      if (!nodeMap.has(rel.source_id)) {
        const { name, sourceText } = getEntityName(rel.source_id, rel.source_type);
        nodeMap.set(rel.source_id, {
          id: rel.source_id,
          name,
          type: rel.source_type,
          val: 3,
          sourceText,
        });
      } else {
        // Increase node size based on number of connections
        const node = nodeMap.get(rel.source_id)!;
        node.val += 1;
      }

      // Add target node
      if (!nodeMap.has(rel.target_id)) {
        const { name, sourceText } = getEntityName(rel.target_id, rel.target_type);
        nodeMap.set(rel.target_id, {
          id: rel.target_id,
          name,
          type: rel.target_type,
          val: 3,
          sourceText,
        });
      } else {
        // Increase node size based on number of connections
        const node = nodeMap.get(rel.target_id)!;
        node.val += 1;
      }

      // Add link (reversed: target uses source, so arrow points target → source)
      links.push({
        source: rel.target_id,
        target: rel.source_id,
        context: rel.relationship_context,
      });
    });

    return {
      nodes: Array.from(nodeMap.values()),
      links,
    };
  }, [relationships, getEntityName]);

  // Check if graph has cycles
  const hasCycles = useMemo(() => {
    return detectCycles(fullGraphData.links);
  }, [fullGraphData.links, detectCycles]);

  // Get all circular dependency paths
  const circularDependencies = useMemo(() => {
    if (!hasCycles) return [];
    return detectCyclesWithPaths(fullGraphData.links);
  }, [fullGraphData.links, hasCycles, detectCyclesWithPaths]);

  // Filter graph data based on selected node and hidden types
  const graphData = useMemo(() => {
    // First, filter nodes based on hidden types
    let filteredNodes = fullGraphData.nodes.filter(node => !hiddenTypes.has(node.type));
    let filteredLinks = fullGraphData.links;

    // If a node is selected, show only directly connected nodes
    if (selectedNodeId) {
      const connectedNodeIds = new Set<string>([selectedNodeId]);
      
      // Find all directly connected nodes
      filteredLinks.forEach(link => {
        if (link.source === selectedNodeId || (typeof link.source === 'object' && (link.source as any).id === selectedNodeId)) {
          const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
          connectedNodeIds.add(targetId);
        }
        if (link.target === selectedNodeId || (typeof link.target === 'object' && (link.target as any).id === selectedNodeId)) {
          const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
          connectedNodeIds.add(sourceId);
        }
      });

      // Filter nodes to only show connected ones (and not hidden)
      filteredNodes = filteredNodes.filter(node => connectedNodeIds.has(node.id));
      
      // Filter links to only show those between visible nodes
      filteredLinks = filteredLinks.filter(link => {
        const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
        const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
        return connectedNodeIds.has(sourceId) && connectedNodeIds.has(targetId);
      });
    }
    
    // Always filter links based on hidden types (applies whether node is selected or not)
    filteredLinks = filteredLinks.filter(link => {
      const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
      const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
      const sourceNode = fullGraphData.nodes.find(n => n.id === sourceId);
      const targetNode = fullGraphData.nodes.find(n => n.id === targetId);
      
      // Hide link if either source or target node type is hidden
      return sourceNode && targetNode && 
             !hiddenTypes.has(sourceNode.type) && 
             !hiddenTypes.has(targetNode.type);
    });

    return {
      nodes: filteredNodes,
      links: filteredLinks,
    };
  }, [fullGraphData, selectedNodeId, hiddenTypes]);

  // Handle node click - toggle selection or navigate
  const handleNodeClick = useCallback((node: any, event: MouseEvent) => {
    const entityType = node.type;
    const entityId = node.id;

    // If Shift key is held, toggle selection for filtering
    if (event.shiftKey) {
      setSelectedNodeId(prev => prev === entityId ? null : entityId);
      return;
    }

    // If onNodeClick callback is provided, use it (for drill-down)
    if (onNodeClick) {
      onNodeClick(entityId, entityType);
      return;
    }

    // Otherwise, navigate to entity
    switch (entityType) {
      case 'glossary':
        navigate(`/library/${entityId}`);
        break;
      case 'document':
        navigate(`/documents/${entityId}`);
        break;
      case 'blueprint':
        navigate(`/templates/${entityId}`);
        break;
      default:
        console.log('No navigation defined for type:', entityType);
    }
  }, [navigate, onNodeClick]);

  // Toggle type visibility
  const toggleTypeVisibility = useCallback((type: string) => {
    setHiddenTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  }, []);

  // Toggle label visibility
  const toggleLabelVisibility = useCallback((type: string) => {
    setHiddenLabels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  }, []);

  // Get display name for entity type
  const getTypeDisplayName = useCallback((type: string): string => {
    switch (type) {
      case 'glossary':
        return 'object';
      case 'blueprint':
        return 'template';
      default:
        return type;
    }
  }, []);

  // Custom node rendering for 2D
  const paintNode = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const label = node.name;
    const fontSize = 12 / globalScale;
    const nodeRadius = Math.sqrt(node.val) * 6;
    const isSelected = selectedNodeId === node.id;
    const showLabel = !hiddenLabels.has(node.type);

    // Theme-aware colors
    const selectionColor = isDarkMode ? '#FFFFFF' : '#000000';
    const borderColor = isDarkMode ? '#1F2937' : '#FFFFFF';
    const labelColor = isDarkMode ? '#E5E7EB' : '#1F2937';

    // Draw node circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI);
    ctx.fillStyle = typeColors[node.type] || '#6B7280';
    ctx.fill();
    
    // Add border (thicker if selected)
    ctx.strokeStyle = isSelected ? selectionColor : borderColor;
    ctx.lineWidth = (isSelected ? 3 : 1.5) / globalScale;
    ctx.stroke();

    // Draw selection ring if selected
    if (isSelected) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeRadius + 4 / globalScale, 0, 2 * Math.PI);
      ctx.strokeStyle = selectionColor;
      ctx.lineWidth = 2 / globalScale;
      ctx.setLineDash([5 / globalScale, 5 / globalScale]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw label only if not hidden
    if (showLabel) {
      ctx.font = `${fontSize}px Inter, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = labelColor;
      ctx.fillText(label, node.x, node.y + nodeRadius + fontSize + 2);
    }
  }, [selectedNodeId, hiddenLabels, isDarkMode]);

  // Custom node rendering for 3D
  const paint3DNode = useCallback((node: any) => {
    const nodeRadius = Math.sqrt(node.val) * 6;
    const isSelected = selectedNodeId === node.id;
    const color = typeColors[node.type] || '#6B7280';
    const selectionColor = isDarkMode ? '#FFFFFF' : '#000000';
    
    // Set node color and size
    node.__threeObj = node.__threeObj || {};
    return {
      color: isSelected ? selectionColor : color,
      size: nodeRadius
    };
  }, [selectedNodeId, isDarkMode]);

  // Create custom node label with tooltip for text items
  const getNodeLabel = useCallback((node: any) => {
    // Check if labels should be shown for this type
    if (hiddenLabels.has(node.type)) {
      return ''; // Return empty string to hide label
    }
    
    if (node.type === 'text' && node.sourceText) {
      return node.sourceText;
    }
    return node.name;
  }, [hiddenLabels]);

  // Calculate dynamic link curvature to prevent overlapping edges
  const getLinkCurvature = useCallback((link: any) => {
    // Find all links between the same two nodes
    const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
    const targetId = typeof link.target === 'object' ? link.target.id : link.target;
    
    const parallelLinks = graphData.links.filter((l: any) => {
      const lSourceId = typeof l.source === 'object' ? l.source.id : l.source;
      const lTargetId = typeof l.target === 'object' ? l.target.id : l.target;
      return (lSourceId === sourceId && lTargetId === targetId) || 
             (lSourceId === targetId && lTargetId === sourceId);
    });
    
    if (parallelLinks.length <= 1) return 0;
    
    // Calculate curvature based on link index
    const linkIndex = parallelLinks.indexOf(link);
    const maxCurvature = 0.5;
    const step = maxCurvature / (parallelLinks.length - 1);
    return (linkIndex * step) - (maxCurvature / 2);
  }, [graphData.links]);

  // Get force configuration based on layout mode
  const getForceConfig = useCallback(() => {
    switch (layoutMode) {
      case 'tight':
        return {
          linkDistance: 50,
          chargeStrength: -150,
          collideRadius: 20,
          centerStrength: 0.3,
        };
      case 'spread':
        return {
          linkDistance: 200,
          chargeStrength: -400,
          collideRadius: 25,
          centerStrength: 0.15,
        };
      case 'hierarchical':
        return {
          linkDistance: 120,
          chargeStrength: -250,
          collideRadius: 22,
          centerStrength: 0.2,
        };
      case 'radial':
        return {
          linkDistance: 150,
          chargeStrength: -300,
          collideRadius: 24,
          centerStrength: 0.1,
        };
      case 'tree':
        return {
          linkDistance: 100,
          chargeStrength: -200,
          collideRadius: 22,
          centerStrength: 0.15,
        };
      case 'default':
      default:
        return {
          linkDistance: 100,
          chargeStrength: -200,
          collideRadius: 22,
          centerStrength: 0.25,
        };
    }
  }, [layoutMode]);

  const forceConfig = getForceConfig();

  // Manual zoom to fit function
  const handleZoomToFit = useCallback(() => {
    if (graphRef.current) {
      graphRef.current.zoomToFit(400, zoomPadding);
    }
  }, [zoomPadding]);

  // Apply custom force configuration on initial load only
  useEffect(() => {
    if (graphRef.current) {
      const fg = graphRef.current;
      
      // Configure d3 forces
      fg.d3Force('link')?.distance(forceConfig.linkDistance);
      fg.d3Force('charge')?.strength(forceConfig.chargeStrength);
      fg.d3Force('collide', forceCollide(forceConfig.collideRadius));
      fg.d3Force('center')?.strength(forceConfig.centerStrength);
    }
  }, [forceConfig]);

  // Handle layout mode changes - gently reheat simulation
  const previousLayoutMode = useRef(layoutMode);
  useEffect(() => {
    if (graphRef.current && previousLayoutMode.current !== layoutMode) {
      const fg = graphRef.current;
      
      // Reheat the simulation to apply new force configuration
      // The d3AlphaDecay and d3VelocityDecay props (0.02 and 0.3) keep it gentle
      if (fg.d3ReheatSimulation) {
        fg.d3ReheatSimulation();
      }
      
      previousLayoutMode.current = layoutMode;
    }
  }, [layoutMode]);

  // Auto-fit graph only on initial mount
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0 && !hasInitiallyZoomed) {
      setTimeout(() => {
        graphRef.current?.zoomToFit(400, zoomPadding);
        setHasInitiallyZoomed(true);
      }, 500);
    }
  }, [graphData, hasInitiallyZoomed, zoomPadding]);

  // Reset zoom flag when graph data changes significantly (nodes added/removed)
  useEffect(() => {
    setHasInitiallyZoomed(false);
  }, [graphData.nodes.length]);

  // Reset DAG mode if graph has cycles
  useEffect(() => {
    if (dagMode && hasCycles) {
      setShowDagError(true);
      setDagMode(null);
      
      // Hide error after 5 seconds
      const timer = setTimeout(() => {
        setShowDagError(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [dagMode, hasCycles]);

  // Calculate node positions based on layout mode
  const calculateFlowLayout = useCallback((nodes: GraphNode[], layoutType: typeof flowLayoutMode) => {
    const positions: { [key: string]: { x: number; y: number } } = {};
    const nodeCount = nodes.length;
    
    switch (layoutType) {
      case 'circular': {
        const radius = Math.max(200, nodeCount * 20);
        const angleStep = (2 * Math.PI) / Math.max(nodeCount, 1);
        nodes.forEach((node, index) => {
          const angle = index * angleStep;
          positions[node.id] = {
            x: Math.cos(angle) * radius,
            y: Math.sin(angle) * radius,
          };
        });
        break;
      }
      
      case 'grid': {
        const cols = Math.ceil(Math.sqrt(nodeCount));
        const spacing = 250;
        nodes.forEach((node, index) => {
          const row = Math.floor(index / cols);
          const col = index % cols;
          positions[node.id] = {
            x: col * spacing,
            y: row * spacing,
          };
        });
        break;
      }
      
      case 'horizontal-tree': {
        // Build adjacency list for hierarchy
        const adjacency = new Map<string, string[]>();
        const inDegree = new Map<string, number>();
        nodes.forEach(n => {
          adjacency.set(n.id, []);
          inDegree.set(n.id, 0);
        });
        
        graphData.links.forEach(link => {
          const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
          const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
          adjacency.get(sourceId)?.push(targetId);
          inDegree.set(targetId, (inDegree.get(targetId) || 0) + 1);
        });
        
        // Find root nodes (no incoming edges)
        const roots = nodes.filter(n => inDegree.get(n.id) === 0);
        const levels = new Map<string, number>();
        const levelNodes = new Map<number, string[]>();
        
        // BFS to assign levels
        const queue = [...roots.map(r => ({ id: r.id, level: 0 }))];
        while (queue.length > 0) {
          const { id, level } = queue.shift()!;
          if (levels.has(id)) continue;
          levels.set(id, level);
          if (!levelNodes.has(level)) levelNodes.set(level, []);
          levelNodes.get(level)!.push(id);
          
          adjacency.get(id)?.forEach(childId => {
            if (!levels.has(childId)) {
              queue.push({ id: childId, level: level + 1 });
            }
          });
        }
        
        // Assign positions for unconnected nodes
        nodes.forEach(node => {
          if (!levels.has(node.id)) {
            const maxLevel = Math.max(...Array.from(levels.values()), 0);
            levels.set(node.id, maxLevel + 1);
            if (!levelNodes.has(maxLevel + 1)) levelNodes.set(maxLevel + 1, []);
            levelNodes.get(maxLevel + 1)!.push(node.id);
          }
        });
        
        // Position nodes
        const horizontalSpacing = 300;
        const verticalSpacing = 150;
        levelNodes.forEach((nodeIds, level) => {
          nodeIds.forEach((nodeId, index) => {
            const yOffset = (index - nodeIds.length / 2) * verticalSpacing;
            positions[nodeId] = {
              x: level * horizontalSpacing,
              y: yOffset,
            };
          });
        });
        break;
      }
      
      case 'vertical-tree': {
        // Similar to horizontal but rotated
        const adjacency = new Map<string, string[]>();
        const inDegree = new Map<string, number>();
        nodes.forEach(n => {
          adjacency.set(n.id, []);
          inDegree.set(n.id, 0);
        });
        
        graphData.links.forEach(link => {
          const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
          const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
          adjacency.get(sourceId)?.push(targetId);
          inDegree.set(targetId, (inDegree.get(targetId) || 0) + 1);
        });
        
        const roots = nodes.filter(n => inDegree.get(n.id) === 0);
        const levels = new Map<string, number>();
        const levelNodes = new Map<number, string[]>();
        
        const queue = [...roots.map(r => ({ id: r.id, level: 0 }))];
        while (queue.length > 0) {
          const { id, level } = queue.shift()!;
          if (levels.has(id)) continue;
          levels.set(id, level);
          if (!levelNodes.has(level)) levelNodes.set(level, []);
          levelNodes.get(level)!.push(id);
          
          adjacency.get(id)?.forEach(childId => {
            if (!levels.has(childId)) {
              queue.push({ id: childId, level: level + 1 });
            }
          });
        }
        
        nodes.forEach(node => {
          if (!levels.has(node.id)) {
            const maxLevel = Math.max(...Array.from(levels.values()), 0);
            levels.set(node.id, maxLevel + 1);
            if (!levelNodes.has(maxLevel + 1)) levelNodes.set(maxLevel + 1, []);
            levelNodes.get(maxLevel + 1)!.push(node.id);
          }
        });
        
        const horizontalSpacing = 200;
        const verticalSpacing = 200;
        levelNodes.forEach((nodeIds, level) => {
          nodeIds.forEach((nodeId, index) => {
            const xOffset = (index - nodeIds.length / 2) * horizontalSpacing;
            positions[nodeId] = {
              x: xOffset,
              y: level * verticalSpacing,
            };
          });
        });
        break;
      }
      
      case 'radial': {
        // Similar to circular but with hierarchy-based radius
        const adjacency = new Map<string, string[]>();
        const inDegree = new Map<string, number>();
        nodes.forEach(n => {
          adjacency.set(n.id, []);
          inDegree.set(n.id, 0);
        });
        
        graphData.links.forEach(link => {
          const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
          const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
          adjacency.get(sourceId)?.push(targetId);
          inDegree.set(targetId, (inDegree.get(targetId) || 0) + 1);
        });
        
        const roots = nodes.filter(n => inDegree.get(n.id) === 0);
        const levels = new Map<string, number>();
        
        const queue = [...roots.map(r => ({ id: r.id, level: 0 }))];
        while (queue.length > 0) {
          const { id, level } = queue.shift()!;
          if (levels.has(id)) continue;
          levels.set(id, level);
          
          adjacency.get(id)?.forEach(childId => {
            if (!levels.has(childId)) {
              queue.push({ id: childId, level: level + 1 });
            }
          });
        }
        
        nodes.forEach(node => {
          if (!levels.has(node.id)) {
            levels.set(node.id, 0);
          }
        });
        
        const levelCounts = new Map<number, number>();
        const levelIndices = new Map<string, number>();
        nodes.forEach(node => {
          const level = levels.get(node.id)!;
          const count = levelCounts.get(level) || 0;
          levelIndices.set(node.id, count);
          levelCounts.set(level, count + 1);
        });
        
        nodes.forEach(node => {
          const level = levels.get(node.id)!;
          const index = levelIndices.get(node.id)!;
          const count = levelCounts.get(level)!;
          const radius = 150 + level * 200;
          const angle = (2 * Math.PI * index) / Math.max(count, 1);
          
          positions[node.id] = {
            x: Math.cos(angle) * radius,
            y: Math.sin(angle) * radius,
          };
        });
        break;
      }
      
      case 'force': {
        // Simple force-directed layout (basic implementation)
        const centerX = 0;
        const centerY = 0;
        const repulsion = 5000;
        const attraction = 0.1;
        const iterations = 100;
        
        // Initialize random positions
        nodes.forEach((node, index) => {
          const angle = (2 * Math.PI * index) / nodeCount;
          positions[node.id] = {
            x: Math.cos(angle) * 300,
            y: Math.sin(angle) * 300,
          };
        });
        
        // Run simulation
        for (let iter = 0; iter < iterations; iter++) {
          const forces: { [key: string]: { x: number; y: number } } = {};
          nodes.forEach(n => forces[n.id] = { x: 0, y: 0 });
          
          // Repulsive forces between all nodes
          for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
              const node1 = nodes[i];
              const node2 = nodes[j];
              const dx = positions[node2.id].x - positions[node1.id].x;
              const dy = positions[node2.id].y - positions[node1.id].y;
              const distance = Math.sqrt(dx * dx + dy * dy) || 1;
              const force = repulsion / (distance * distance);
              
              forces[node1.id].x -= (dx / distance) * force;
              forces[node1.id].y -= (dy / distance) * force;
              forces[node2.id].x += (dx / distance) * force;
              forces[node2.id].y += (dy / distance) * force;
            }
          }
          
          // Attractive forces for connected nodes
          graphData.links.forEach(link => {
            const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
            const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
            const dx = positions[targetId].x - positions[sourceId].x;
            const dy = positions[targetId].y - positions[sourceId].y;
            const distance = Math.sqrt(dx * dx + dy * dy) || 1;
            
            forces[sourceId].x += dx * attraction;
            forces[sourceId].y += dy * attraction;
            forces[targetId].x -= dx * attraction;
            forces[targetId].y -= dy * attraction;
          });
          
          // Apply forces
          nodes.forEach(node => {
            positions[node.id].x += forces[node.id].x;
            positions[node.id].y += forces[node.id].y;
          });
        }
        break;
      }
    }
    
    return positions;
  }, [graphData]);

  // Convert graph data to React Flow format with automatic layout
  const { nodes: flowNodes, edges: flowEdges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    
    const positions = calculateFlowLayout(graphData.nodes, flowLayoutMode);
    
    graphData.nodes.forEach((node) => {
      const position = positions[node.id];
      const isSelected = selectedNodeId === node.id;
      const isHidden = hiddenTypes.has(node.type);
      const showLabel = !hiddenLabels.has(node.type);
      
      nodes.push({
        id: node.id,
        type: 'default',
        position,
        data: { 
          label: showLabel ? node.name : '',
          fullText: node.sourceText || node.name,
          type: node.type,
        },
        hidden: isHidden,
        style: {
          background: typeColors[node.type] || '#6B7280',
          color: '#FFFFFF',
          border: isSelected ? '3px solid #000000' : '2px solid #FFFFFF',
          borderRadius: '8px',
          minWidth: '120px',
          maxWidth: '200px',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '13px',
          fontWeight: '500',
          textAlign: 'center',
          boxShadow: isSelected ? '0 0 0 4px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.1)',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
        },
      });
    });
    
    graphData.links.forEach((link, index) => {
      const sourceId = typeof link.source === 'string' ? link.source : (link.source as any).id;
      const targetId = typeof link.target === 'string' ? link.target : (link.target as any).id;
      
      edges.push({
        id: `edge-${index}`,
        source: sourceId,
        target: targetId,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#94A3B8', strokeWidth: 1 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#94A3B8',
          width: 15,
          height: 15,
        },
      });
    });
    
    return { nodes, edges };
  }, [graphData, selectedNodeId, hiddenTypes, hiddenLabels, flowLayoutMode, calculateFlowLayout]);

  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);

  // Update nodes and edges when data changes
  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  // Handle node click in React Flow
  const handleFlowNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    const entityType = node.data.type;
    const entityId = node.id;

    // If Shift key is held, toggle selection for filtering
    if (event.shiftKey) {
      setSelectedNodeId(prev => prev === entityId ? null : entityId);
      return;
    }

    // If onNodeClick callback is provided, use it (for drill-down)
    if (onNodeClick) {
      onNodeClick(entityId, entityType);
      return;
    }

    // Otherwise, navigate to entity
    switch (entityType) {
      case 'glossary':
        navigate(`/library/${entityId}`);
        break;
      case 'document':
        navigate(`/documents/${entityId}`);
        break;
      case 'blueprint':
        navigate(`/templates/${entityId}`);
        break;
      default:
        console.log('No navigation defined for type:', entityType);
    }
  }, [navigate, onNodeClick]);

  if (graphData.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-muted-foreground text-lg mb-2">{t('platform.graphs.entityrelationshipgraph.no_relationships_found')}</p>
          <p className="text-muted-foreground/70 text-sm">
            Relationships will appear here once entities reference each other
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-hidden">
      {/* DAG Error Notification */}
      {showDagError && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 bg-red-50 dark:bg-red-950/30 border-2 border-red-400 dark:border-red-800 rounded px-6 py-3 max-w-md">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1 text-center">{t('platform.graphs.entityrelationshipgraph.dag_mode_unavailable')}</h4>
              <p className="text-xs text-red-700 dark:text-red-300 text-center">
                This graph contains circular relationships. DAG (Directed Acyclic Graph) modes only work with hierarchical structures without cycles.
              </p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium text-center">
                Try using "Spread Layout" or "Hierarchical" layout modes instead.
              </p>
            </div>
            <button
              onClick={() => setShowDagError(false)}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex-shrink-0"
              title={t('platform.graphs.entityrelationshipgraph.dismiss')}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Circular Dependencies Overview Panel */}
      {showCyclesOverview && hasCycles && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30 bg-card border-2 border-yellow-400 dark:border-yellow-600 rounded max-w-2xl max-h-[70vh] overflow-hidden flex flex-col">
          <div className="bg-yellow-50 dark:bg-yellow-950/30 border-b border-yellow-200 dark:border-yellow-800 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-lg font-bold text-yellow-900 dark:text-yellow-200">{t('platform.graphs.entityrelationshipgraph.circular_dependencies')}</h3>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">Found {circularDependencies.length} circular reference(s)</p>
              </div>
            </div>
            <button
              onClick={() => setShowCyclesOverview(false)}
              className="text-yellow-700 dark:text-yellow-300 hover:text-yellow-900 dark:hover:text-yellow-100 transition-colors"
              title={t('platform.common.close')}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          <div className="overflow-y-auto p-6">
            <p className="text-sm text-muted-foreground mb-4">
              The following circular relationships prevent DAG mode from being used. 
              To enable DAG mode, you need to break at least one relationship in each cycle.
            </p>
            
            <div className="space-y-4">
              {circularDependencies.map((cycle, cycleIndex) => {
                const uniqueNodes = cycle.slice(0, -1); // Remove the duplicate last node
                return (
                  <div 
                    key={cycleIndex}
                    className="bg-muted border border-border rounded p-4"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded">
                        Cycle {cycleIndex + 1}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {uniqueNodes.length} entities in loop
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      {cycle.map((nodeId, nodeIndex) => {
                        const node = fullGraphData.nodes.find(n => n.id === nodeId);
                        const isLastNode = nodeIndex === cycle.length - 1;
                        const isStartNode = nodeIndex === 0;
                        
                        return (
                          <div key={`${cycleIndex}-${nodeIndex}`}>
                            <div 
                              className={`flex items-center gap-3 p-2 rounded transition-colors ${
                                isLastNode && isStartNode 
                                  ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700'
                                  : 'hover:bg-muted-foreground/10'
                              }`}
                              onClick={(e) => {
                                if (!isLastNode) {
                                  handleNodeClick(node, e.nativeEvent);
                                }
                              }}
                              style={{ cursor: isLastNode ? 'default' : 'pointer' }}
                            >
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: node ? typeColors[node.type] || '#6B7280' : '#6B7280' }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">
                                  {node?.name || 'Unknown'}
                                </p>
                                <p className="text-xs text-muted-foreground capitalize">
                                  {node ? getTypeDisplayName(node.type) : 'unknown'}
                                </p>
                              </div>
                              {isLastNode && isStartNode && (
                                <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300 bg-yellow-200 dark:bg-yellow-800 px-2 py-0.5 rounded">
                                  Loop back
                                </span>
                              )}
                            </div>
                            
                            {!isLastNode && (
                              <div className="flex items-center justify-center py-1">
                                <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2 text-center">{t('platform.graphs.entityrelationshipgraph.how_to_fix')}</h4>
              <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1 list-disc list-inside">
                <li>{t('platform.graphs.entityrelationshipgraph.click_on_any_entity_in_a_cycle_to_navigate_to_it')}</li>
                <li>{t('platform.graphs.entityrelationshipgraph.review_and_remove_unnecessary_relationships')}</li>
                <li>{t('platform.graphs.entityrelationshipgraph.reorganize_your_entity_structure_to_be_hierarchical')}</li>
                <li>{t('platform.graphs.entityrelationshipgraph.after_breaking_all_cycles_dag_mode_will_become_available')}</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Control Buttons */}
      {showControls && (
        <div className="absolute top-4 left-4 z-10 flex flex-col gap-2 max-w-xs">
          {/* View Mode Selector */}
          <div className="bg-card border border-border rounded shadow-sm">
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as any)}
              className="w-full px-4 py-2 text-sm font-medium rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer text-center"
            >
              <option value="force-2d">{t('platform.graphs.entityrelationshipgraph.force_2d_view')}</option>
              <option value="force-3d">{t('platform.graphs.entityrelationshipgraph.force_3d_view')}</option>
              <option value="flow">{t('platform.graphs.entityrelationshipgraph.react_flow_view')}</option>
            </select>
          </div>
          
          {/* React Flow Layout Selector - Only for React Flow view */}
          {viewMode === 'flow' && (
            <div className="bg-card border border-border rounded shadow-sm">
              <select
                value={flowLayoutMode}
                onChange={(e) => setFlowLayoutMode(e.target.value as any)}
                className="w-full px-4 py-2 text-sm font-medium rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer text-center"
              >
                <option value="vertical-tree">{t('platform.graphs.entityrelationshipgraph.vertical_tree')}</option>
                <option value="horizontal-tree">{t('platform.graphs.entityrelationshipgraph.horizontal_tree')}</option>
                <option value="radial">{t('platform.graphs.entityrelationshipgraph.radial_layout')}</option>
                <option value="circular">{t('platform.graphs.entityrelationshipgraph.circular_layout')}</option>
                <option value="grid">{t('platform.graphs.entityrelationshipgraph.grid_layout')}</option>
                <option value="force">{t('platform.graphs.entityrelationshipgraph.force_directed')}</option>
              </select>
            </div>
          )}
          
          {/* Layout Mode Selector - Only for force graph views */}
          {viewMode !== 'flow' && (
            <div className="bg-card border border-border rounded shadow-sm">
              <select
                value={layoutMode}
                onChange={(e) => setLayoutMode(e.target.value as any)}
                className="w-full px-4 py-2 text-sm font-medium rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer text-center"
              >
                <option value="default">{t('platform.graphs.entityrelationshipgraph.default_layout')}</option>
                <option value="tight">{t('platform.graphs.entityrelationshipgraph.tight_layout')}</option>
                <option value="spread">{t('platform.graphs.entityrelationshipgraph.spread_layout')}</option>
                <option value="hierarchical">{t('platform.graphs.entityrelationshipgraph.hierarchical')}</option>
                <option value="radial">{t('platform.graphs.entityrelationshipgraph.radial')}</option>
                <option value="tree">{t('platform.graphs.entityrelationshipgraph.tree')}</option>
              </select>
            </div>
          )}
          
           {/* DAG Mode Selector - Only for force graph views */}
           {viewMode !== 'flow' && (
             <div className="bg-card border border-border rounded shadow-sm">
               <select
                 value={dagMode || 'none'}
                 onChange={(e) => setDagMode(e.target.value === 'none' ? null : e.target.value as any)}
                 className="w-full px-4 py-2 text-sm font-medium rounded bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed"
                 title={hasCycles ? "Directed Acyclic Graph (DAG) mode is disabled because the graph contains circular relationships" : "Directed Acyclic Graph (DAG) layout modes organize nodes hierarchically"}
                 disabled={hasCycles}
               >
                 <option value="none">{hasCycles ? 'DAG Mode (Unavailable)' : 'No DAG Mode'}</option>
                 <option value="td" disabled={hasCycles}>{t('platform.graphs.entityrelationshipgraph.top_down')}</option>
                 <option value="bu" disabled={hasCycles}>{t('platform.graphs.entityrelationshipgraph.bottom_up')}</option>
                 <option value="lr" disabled={hasCycles}>{t('platform.graphs.entityrelationshipgraph.left_right')}</option>
                 <option value="rl" disabled={hasCycles}>{t('platform.graphs.entityrelationshipgraph.right_left')}</option>
                 <option value="radialout" disabled={hasCycles}>{t('platform.graphs.entityrelationshipgraph.radial_outward')}</option>
                 <option value="radialin" disabled={hasCycles}>{t('platform.graphs.entityrelationshipgraph.radial_inward')}</option>
               </select>
             </div>
           )}
          
          {/* Zoom to Fit Button - Only for force graph views */}
          {viewMode !== 'flow' && (
            <button
              onClick={handleZoomToFit}
              className="bg-card border border-border rounded px-4 py-2 hover:bg-muted transition-colors font-medium text-sm shadow-sm text-foreground"
              title={t('platform.graphs.entityrelationshipgraph.fit_graph_to_screen')}
            >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
              </svg>
              Fit to Screen
            </span>
          </button>
          )}
        </div>
      )}

      {viewMode === 'flow' ? (
        <div className="w-full h-full">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleFlowNodeClick}
            fitView
            attributionPosition="bottom-left"
            className="bg-card"
            minZoom={0.1}
            maxZoom={4}
          >
            <Background />
            <Controls />
            <MiniMap 
              nodeColor={(node: any) => node.style?.background || '#6B7280'}
              maskColor="rgba(0, 0, 0, 0.1)"
            />
            <Panel position="top-right" className="bg-card/90 backdrop-blur-sm p-2 rounded text-xs text-muted-foreground">
              React Flow View
            </Panel>
          </ReactFlow>
        </div>
      ) : viewMode === 'force-3d' ? (
        disableZoomCompensation ? (
          <ForceGraph3D
            ref={graphRef}
            width={width}
            height={height}
            graphData={graphData}
            backgroundColor={isDarkMode ? '#1a1a2e' : 'white'}
            nodeLabel={getNodeLabel}
            nodeAutoColorBy="type"
            nodeColor={(node: any) => typeColors[node.type] || '#6B7280'}
            nodeVal="val"
            onNodeClick={handleNodeClick}
            linkDirectionalArrowLength={4}
            linkDirectionalArrowRelPos={1}
            linkCurvature={getLinkCurvature}
            linkColor={() => '#94A3B8'}
            linkWidth={1.5}
            linkOpacity={0.6}
            linkDirectionalParticles={0}
            cooldownTicks={100}
            warmupTicks={100}
            enableNodeDrag={true}
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.3}
            dagMode={dagMode}
            dagLevelDistance={dagMode ? 100 : undefined}
          />
        ) : (
          <div style={{ 
            width: width, 
            height: height,
            transform: `scale(${1 / zoom})`,
            transformOrigin: 'top left',
            position: 'relative'
          }}>
            <ForceGraph3D
              ref={graphRef}
              width={width * zoom}
              height={height * zoom}
              graphData={graphData}
              backgroundColor={isDarkMode ? '#1a1a2e' : 'white'}
              nodeLabel={getNodeLabel}
              nodeAutoColorBy="type"
              nodeColor={(node: any) => typeColors[node.type] || '#6B7280'}
              nodeVal="val"
              onNodeClick={handleNodeClick}
              linkDirectionalArrowLength={4}
              linkDirectionalArrowRelPos={1}
              linkCurvature={getLinkCurvature}
              linkColor={() => '#94A3B8'}
              linkWidth={1.5}
              linkOpacity={0.6}
              linkDirectionalParticles={0}
              cooldownTicks={100}
              warmupTicks={100}
              enableNodeDrag={true}
              d3AlphaDecay={0.02}
              d3VelocityDecay={0.3}
              dagMode={dagMode}
              dagLevelDistance={dagMode ? 100 : undefined}
            />
          </div>
        )
      ) : (
        disableZoomCompensation ? (
          <ForceGraph2D
            ref={graphRef}
            width={width}
            height={height}
            graphData={graphData}
            nodeLabel={getNodeLabel}
            nodeAutoColorBy="type"
            nodeCanvasObject={paintNode}
            nodeVal="val"
            onNodeClick={handleNodeClick}
            linkDirectionalArrowLength={4}
            linkDirectionalArrowRelPos={1}
            linkCurvature={getLinkCurvature}
            linkColor={() => '#94A3B8'}
            linkWidth={1.5}
            linkDirectionalParticles={0}
            cooldownTicks={100}
            warmupTicks={100}
            enableNodeDrag={true}
            enableZoomInteraction={true}
            enablePanInteraction={true}
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.3}
            dagMode={dagMode}
            dagLevelDistance={dagMode ? 100 : undefined}
          />
        ) : (
          <div style={{ 
            width: width, 
            height: height,
            transform: `scale(${1 / zoom})`,
            transformOrigin: 'top left',
            position: 'relative'
          }}>
            <ForceGraph2D
              ref={graphRef}
              width={width * zoom}
              height={height * zoom}
              graphData={graphData}
              nodeLabel={getNodeLabel}
              nodeAutoColorBy="type"
              nodeCanvasObject={paintNode}
              nodeVal="val"
              onNodeClick={handleNodeClick}
              linkDirectionalArrowLength={4}
              linkDirectionalArrowRelPos={1}
              linkCurvature={getLinkCurvature}
              linkColor={() => '#94A3B8'}
              linkWidth={1.5}
              linkDirectionalParticles={0}
              cooldownTicks={100}
              warmupTicks={100}
              enableNodeDrag={true}
              enableZoomInteraction={true}
              enablePanInteraction={true}
              d3AlphaDecay={0.02}
              d3VelocityDecay={0.3}
              dagMode={dagMode}
              dagLevelDistance={dagMode ? 100 : undefined}
            />
          </div>
        )
      )}

      {/* Legend */}
      {showLegend && (
        <div className="absolute top-4 right-4 bg-card border border-border rounded p-4 ">
          <h3 className="text-sm font-semibold mb-3 text-foreground text-center">{t('platform.graphs.entityrelationshipgraph.entity_types')}</h3>
          
          {/* Cycle Detection Info */}
          {hasCycles && (
            <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200 text-center">{t('platform.graphs.entityrelationshipgraph.circular_references_detected')}</p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5 text-center">{t('platform.graphs.entityrelationshipgraph.dag_modes_disabled')}</p>
                  <button
                    onClick={() => setShowCyclesOverview(!showCyclesOverview)}
                    className="w-full mt-2 px-2 py-1 bg-yellow-100 dark:bg-yellow-800 hover:bg-yellow-200 dark:hover:bg-yellow-700 text-yellow-800 dark:text-yellow-200 text-xs rounded border border-yellow-300 dark:border-yellow-600 transition-colors font-medium"
                  >
                    {showCyclesOverview ? 'Hide' : 'View'} Circular Dependencies ({circularDependencies.length})
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {Object.entries(typeColors).map(([type, color]) => {
              const isHidden = hiddenTypes.has(type);
              const isLabelHidden = hiddenLabels.has(type);
              const typeCount = fullGraphData.nodes.filter(n => n.type === type).length;
              const displayName = getTypeDisplayName(type);
              
              return (
                <div 
                  key={type} 
                  className="p-1 rounded hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full border-2 transition-all cursor-pointer"
                      style={{ 
                        backgroundColor: isHidden ? '#E5E7EB' : color,
                        borderColor: isHidden ? '#9CA3AF' : color
                      }}
                      onClick={() => toggleTypeVisibility(type)}
                      title={isHidden ? `Show ${displayName} nodes` : `Hide ${displayName} nodes`}
                    />
                    <span className={`text-sm capitalize flex-1 ${isHidden ? 'text-muted-foreground/50 line-through' : 'text-muted-foreground'}`}>
                      {displayName} ({typeCount})
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLabelVisibility(type);
                      }}
                      className={`text-xs px-2 py-0.5 rounded border transition-all ${
                        isLabelHidden 
                          ? 'bg-muted text-muted-foreground/50 border-border hover:bg-muted/80' 
                          : 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20'
                      }`}
                      title={isLabelHidden ? 'Click to show text labels' : 'Click to hide text labels'}
                    >
                      <span className={isLabelHidden ? 'line-through' : ''}>Aa</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          {selectedNodeId && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t('platform.graphs.entityrelationshipgraph.filtering_direct_connections')}</span>
                <button
                  onClick={() => setSelectedNodeId(null)}
                  className="text-xs text-primary hover:text-primary/80 font-medium"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
          
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Click nodes to navigate
            </p>
            <p className="text-xs text-muted-foreground mt-1 text-center">
              Shift+Click to filter connections
            </p>
            <p className="text-xs text-muted-foreground mt-1 text-center">
              Click ● to show/hide nodes
            </p>
            <p className="text-xs text-muted-foreground mt-1 text-center">
              Click <span className="bg-primary/10 px-1 rounded border border-primary/30 text-primary">Aa</span> to toggle labels
            </p>
            <p className="text-xs text-muted-foreground mt-1 text-center">
              Drag to pan • Scroll to zoom
            </p>
            {dagMode && (
              <p className="text-xs text-primary mt-2 font-medium text-center">
                DAG Mode: Hierarchical layout active
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

